import { useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Outlet, useLocation } from 'react-router-dom';

import Sidebar from 'components/Sidebar';
import AppHeader from 'components/AppHeader';

import { TopbarHeight } from 'theme/variables';
import AppContextProvider, { useAppContext } from 'context/AppContextProvider';

const MainWrapper = styled('div')(() => ({
  display: 'flex',
  minHeight: '100vh',
  overflow: 'hidden',
  width: '100%',
  position: 'relative',
}));

const PageWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flex: '1 1 auto',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.container,
  position: 'relative',
  width: '100%',
  minWidth: 0, // Critical: allows flex child to shrink below content size
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  [theme.breakpoints.up('lg')]: {
    paddingTop: TopbarHeight,
    marginLeft: 0,
  },
  [theme.breakpoints.down('lg')]: {
    paddingTop: '64px',
    marginLeft: 0,
  },
}));

const MainContent = styled('div')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  width: '100%',
  maxWidth: '100%', // Prevents content from expanding beyond viewport
  minWidth: 0, // Critical: allows flex child to shrink below content size
  boxSizing: 'border-box', // Include padding in width calculation
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: 0,
  marginRight: 0,
  [theme.breakpoints.up('lg')]: {
    marginLeft: 0,
    marginRight: 0,
  },
  [theme.breakpoints.down('lg')]: {
    marginLeft: 0,
    marginRight: 0,
  },
}));

const AppLayoutContent = () => {
  const location = useLocation();
  const { isSidebarOpen, isMobileSidebarOpen, setMobileSidebarOpen } =
    useAppContext();

  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const isMessagesPage = location.pathname === '/messages';

  return (
    <MainWrapper>
      <AppHeader isDesktop={isDesktop} />
      <Sidebar
        isDesktop={isDesktop}
        isSidebarOpen={isSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        onSidebarClose={() => setMobileSidebarOpen(false)}
      />
      <PageWrapper>
        <MainContent
          sx={{
            ...(isMessagesPage && {
              padding: 0,
              width: '100%',
            }),
          }}
        >
          <Outlet />
        </MainContent>
      </PageWrapper>
    </MainWrapper>
  );
};

const AppLayout = () => {
  return (
    <AppContextProvider>
      <AppLayoutContent />
    </AppContextProvider>
  );
};

export default AppLayout;
